import { useElementEventsTracker } from "@/hooks/useElementEventsTracker";
import { isServer, captureError } from "./common.helpers";
import { Dialect } from "@/types/setting.types";
import { getAvailableDialects } from "@/actions/availableDialects.actions";

const keys = {
  startProfileLoader: "startProfileLoader",
  stopProfileLoader: "stopProfileLoader",
};

/**
 * For Home Virtual List
 * Tracks indices of widgets that are not visible to users (e.g. empty widgets or failed to load).
 * This is used to calculate accurate widget positions when tracking analytics.
 * For example, if widget at index 2 is invisible, widget at index 3 should report position 2.
 * @see useElementEventsTracker.ts getWidgetVisibleIndex() for implementation details
 */
export const getInvisibleWidgetsIndex = () => {
  if (isServer()) return;
  return window.indexNotVisible || [];
};

export const setInvisibleWidgetsIndex = (index: number) => {
  if (isServer()) return;
  if (!window.indexNotVisible) {
    window.indexNotVisible = [];
  }
  if (!window.indexNotVisible.includes(index)) {
    window.indexNotVisible = [...window.indexNotVisible, index];
  }
};

export const removeInvisibleWidgetsIndex = (index: number) => {
  if (
    isServer() ||
    !window.indexNotVisible ||
    !Array.isArray(window.indexNotVisible)
  ) {
    return;
  }
  window.indexNotVisible = window.indexNotVisible.filter(
    (i: number) => i !== index,
  );
};

export const clearInvisibleWidgetsIndex = () => {
  if (isServer()) return;
  window.indexNotVisible = [];
};

export const windowHelpers = {
  // profile loader 👇
  setStartProfileLoader: (startProfileLoader: () => void) => {
    if (isServer()) return;
    window[keys.startProfileLoader] = startProfileLoader;
  },
  setStopProfileLoader: (stopProfileLoader: () => void) => {
    if (isServer()) return;
    window[keys.stopProfileLoader] = stopProfileLoader;
  },
  clearStartProfileLoader: () => {
    if (isServer() || !window[keys.startProfileLoader]) return;
    window[keys.startProfileLoader] = undefined;
  },
  clearStopProfileLoader: () => {
    if (isServer() || !window[keys.stopProfileLoader]) return;
    window[keys.stopProfileLoader] = undefined;
  },
  startProfileLoader: () => {
    if (isServer() || !window[keys.startProfileLoader]) return;
    window[keys.startProfileLoader]();
  },
  stopProfileLoader: () => {
    if (isServer() || !window[keys.stopProfileLoader]) return;
    window[keys.stopProfileLoader]();
  },

  // source of profile selection
  setSourceOfProfileSelection: (sourceOfProfileSelection: string) => {
    if (isServer()) return;
    window.sourceOfProfileSelection = sourceOfProfileSelection;
  },
  getSourceOfProfileSelection: (): string | undefined => {
    if (isServer()) return;
    return window.sourceOfProfileSelection;
  },

  // available dialects
  setAvailableDialects: (availableDialects: Dialect[]) => {
    if (isServer()) return;
    window.availableDialects = availableDialects;
  },
  getAvailableDialects: async (): Promise<Dialect[] | undefined> => {
    if (isServer()) return [];
    const cachedAvailableDialects = window.availableDialects;
    if (!cachedAvailableDialects) {
      const availableDialects = await getAvailableDialects();
      window.availableDialects = availableDialects;
      return availableDialects;
    }
    return cachedAvailableDialects;
  },
};
/**
 * On click of Content Detail Action opens a new Ui
 * Tracks the current action page name and page state
 */
export const getContentDetailCurrentModalName = () => {
  if (isServer()) return;
  return window.currentModalName;
};

export const setContentDetailCurrentModalName = (modalName: string) => {
  if (isServer()) return;
  window.currentModalName = modalName;
};

export const clearContentDetailCurrentModalName = () => {
  if (isServer()) return;
  window.currentModalName = undefined;
};

export const getIsContentDetailModalOpened = () => {
  if (isServer()) return;
  return window.isContentDetailModalOpened || false;
};

export const setIsContentDetailModalOpened = (isOpened: boolean) => {
  if (isServer()) return;
  window.isContentDetailModalOpened = isOpened;
};

export const clearIsContentDetailModalOpened = () => {
  if (isServer()) return;
  window.isContentDetailModalOpened = false;
};

/**
 * Tracks whether Statsig has been initialized and is ready to use
 */
export const getStatsigReady = () => {
  if (isServer()) return;
  return window.statsigReady || false;
};

export const setStatsigReady = (isReady: boolean) => {
  if (isServer()) return;
  window.statsigReady = isReady;
};

export const clearStatsigReady = () => {
  if (isServer()) return;
  window.statsigReady = false;
};

/**
 * Opens a URL in a new tab with security best practices (noopener, noreferrer)
 * @param {string} url - The URL to open in a new tab
 * @returns {boolean} - True if URL was opened successfully, false otherwise
 */
export const openInNewTab = (url: string): boolean => {
  if (isServer()) return false;
  try {
    const validatedUrl = new URL(url);
    if (!["http:", "https:"].includes(validatedUrl.protocol)) {
      return false;
    }
    window.open(validatedUrl.href, "_blank", "noopener,noreferrer");
    return true;
  } catch (error) {
    captureError(error, { context: "openInNewTab", url });
    return false;
  }
};
